import React, { useEffect, useRef } from 'react';
import { CheckIcon, GiftIcon, LockIcon } from 'lucide-react';
import { bonuses, offer, price, regularPrice } from '../data/content';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton } from './CtaButton';
import { ImageSlot } from './ImageSlot';
import { SectionHeading } from './SectionHeading';
type OfferSectionProps = {
  offerMockupUrl: string;
};
export function OfferSection({
  offerMockupUrl
}: OfferSectionProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const observer = new IntersectionObserver(entries => {
      if (!fired && entries.some(e => e.isIntersecting)) {
        fired = true;
        trackPixel('ViewContent');
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} id="offer" aria-labelledby="offer-title" className="scroll-mt-0 bg-brand py-12 md:py-20" data-id="element-146" data-mp="12ojzklxzckqi">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-147" data-mp="xw0cmydekd91">
        <SectionHeading id="offer-title" tone="dark" eyebrow={offer.eyebrow} title={offer.title} data-id="element-148" data-mp="sxfjtk0hcvum" />

        <div className="mx-auto mt-8 max-w-[820px] overflow-hidden rounded-2xl bg-white md:mt-12" data-id="element-149" data-mp="owdkz4ah6lt6">
          {/* The mockup has a pure white background, so this area stays white */}
          <div className="aspect-[4/3] bg-white px-2 pt-3 sm:aspect-[16/10] sm:px-6 sm:pt-6" data-id="element-150" data-mp="1lk098exeaxvj">
            <ImageSlot url={offerMockupUrl} alt="Everything included in Team Meeting Ready™" slotName="offerMockupUrl" fit="contain" data-id="element-151" data-mp="1glgpml69xy6c" />
          </div>

          <div className="p-4 pb-6 sm:p-8 md:p-10" data-id="element-152" data-mp="17ljwo7cuqd5h">
            <div className="text-center" data-id="element-153" data-mp="16j7qlm8uv8e">
              <p className="font-display text-[12px] font-black tracking-[0.14em] text-coral-ink" data-id="element-154" data-mp="17ki0nvfrdp2">{offer.kicker}</p>
              <h3 className="mt-1 font-display text-[26px] font-black uppercase leading-tight tracking-tight text-brand md:text-[32px]" data-id="element-155" data-mp="3sb98ebi9dity">{offer.name}</h3>
              <p className="mx-auto mt-2 max-w-[560px] text-[16px] leading-relaxed text-ink/75" data-id="element-156" data-mp="2ng760yhr8c6">{offer.description}</p>
            </div>

            <div className="mt-8 grid gap-7 md:grid-cols-2 md:gap-10" data-id="element-157" data-mp="3ksjii3f1wgl1">
              <div data-id="element-158" data-mp="3oudcjsuqqmc8">
                <h4 className="font-display text-[13px] font-black tracking-[0.1em] text-brand" data-id="element-159" data-mp="3otv6k4ia0tr4">{offer.includedTitle}</h4>
                <ul className="mt-4 space-y-3" data-id="element-160" data-mp="3b26yccb5epff">
                  {offer.included.map(item => <li key={item} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-161" data-mp="3fvjc1reahbte">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-leaf text-white" data-id="element-162" data-mp="316wtzhxa25yh">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-163" data-mp="2s4nnd354qwqd" />
                      </span>
                      {item}
                    </li>)}
                </ul>
              </div>
              <div className="border-t border-hair pt-7 md:border-l md:border-t-0 md:pl-10 md:pt-0" data-id="element-164" data-mp="2dh5gby43hq0s">
                <h4 className="font-display text-[13px] font-black tracking-[0.1em] text-brand" data-id="element-165" data-mp="28jnhj0jskr6i">{offer.bonusesTitle}</h4>
                <ul className="mt-4 space-y-3" data-id="element-166" data-mp="2mjes0rb3npfr">
                  {bonuses.items.map(b => <li key={b.label} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-167" data-mp="2ig8nbo3my6be">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sun text-ink" data-id="element-168" data-mp="2wfi0eewjokb5">
                        <GiftIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-169" data-mp="2rflv8cc42mu0" />
                      </span>
                      <span data-id="element-170" data-mp="317j77lay1its">{b.title}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Price */}
            <div className="mt-8 rounded-xl bg-sun-soft px-3 py-6 text-center sm:px-8" data-id="element-171" data-mp="3f7kd5f62fgg4">
              <p className="font-display text-[12px] font-black tracking-[0.1em] text-ink/80" style={{
              textWrap: 'balance'
            } as React.CSSProperties} data-id="element-172" data-mp="3a9sg66qkd452">
                {offer.packLabel}
              </p>
              <p className="mt-3 text-[14px] font-semibold text-ink/60" data-id="element-173" data-mp="1dgada4hx1bej">
                {offer.regularLabel} <s className="decoration-coral decoration-2" data-id="element-174" data-mp="1i9d8si696uxd">{regularPrice}</s>
              </p>
              <p className="mt-1 font-display text-[4.6rem] font-black leading-none tracking-tight text-brand md:text-[5.6rem]" data-id="element-175" data-mp="1m9x73p2kc4l3">{price}</p>
              <p className="mt-2 text-[13px] font-semibold text-ink/75" data-id="element-176" data-mp="uj97ihgjf79j">{offer.priceNote}</p>
            </div>

            {/* The ONLY checkout button on the page */}
            <div className="mx-auto mt-6 max-w-[440px]" data-id="element-177" data-mp="yjj9lj17ldj4">
              <CtaButton action="checkout" align="center" data-id="element-178" data-mp="13k7bizqz7es4" />
            </div>
            <div className="mt-4 text-center" data-id="element-179" data-mp="2a09mfl0513lp">
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-ink/75" data-id="element-180" data-mp="29z80umlhwypq">
                <LockIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" data-id="element-181" data-mp="2ex9gexjpqfwg" />
                {offer.ctaNote[0]}
              </p>
              <p className="mt-1 text-[13px] text-ink/65" data-id="element-182" data-mp="2k47wor52eu4x">{offer.ctaNote[1]}</p>
              <p className="mt-1 text-[13px] font-semibold text-leaf-ink" data-id="element-183" data-mp="2k6ajz3eu295y">{offer.guaranteeNote}</p>
            </div>

            <p className="mx-auto mt-5 max-w-[460px] text-center text-[13px] leading-relaxed text-ink/60" data-id="element-184" data-mp="2bbw6959yb6oy">{offer.format}</p>
          </div>
        </div>
      </div>
    </section>;
}